import { useCallback, useRef, useState } from "react";
import { useEditorState } from "@tiptap/react";
import type { Editor } from "@tiptap/core";
import { type ToolbarButtonId } from "../shared/richTextEditorToolbarConstants";
import { TooltipProvider } from "@/components/atoms/tooltip";
import { getModKeyLabel } from "@/hooks/modKeyLabel";
import { KeyboardShortcutsModal } from "../../modals/keyboardShortcutsModal";
import { DirectiveReferenceModal } from "../../directives/ui/referenceModal";
import { MarkdownHelpModal } from "../../modals/markdownHelpModal";
import { getAvailableDirectives } from "../../directives/core/config";
import { Keyboard, Asterisk, AtSign } from "lucide-react";
import type { Variables } from "@/lib/sparxs/metadataSchemas";
import { htmlToMarkdown } from "@/lib/markdown/sparxMarkdownHtml";
import { ToolbarButton } from "../shared/richTextEditorToolbarButton";
import { getRteShortcutKeys } from "../shared/richTextEditorToolbarShortcuts";
import { selectRichTextToolbarActiveState } from "../shared/selectRichTextToolbarActiveState";
import {
  richTextEditorHeaderToolbarButtons,
  type RichTextEditorToolbarStateSnapshot,
} from "./richTextEditorHeaderToolbarButtons";

export interface RichTextEditorHeaderProps {
  editor: Editor | null;
  isWysiwyg: boolean;
  buttons: ToolbarButtonId[];
  variables: Variables[];
  onChange: (markdown: string) => void;
  /** When false, hides trailing shortcuts / Markdown help controls in the header. */
  showTrailingHelp?: boolean;
}

const selectHeaderToolbarState = (snapshot: {
  editor: Editor | null;
}): RichTextEditorToolbarStateSnapshot | null => {
  const active = selectRichTextToolbarActiveState(snapshot);
  const { editor } = snapshot;
  if (active == null || editor == null) {
    return null;
  }
  return {
    ...active,
    canUndo: editor.can().undo(),
    canRedo: editor.can().redo(),
  };
};

export const RichTextEditorHeader = ({
  editor,
  isWysiwyg,
  buttons,
  variables,
  onChange,
  showTrailingHelp = true,
}: RichTextEditorHeaderProps) => {
  const modKeyLabel = getModKeyLabel();
  const shortcutKeys = getRteShortcutKeys(modKeyLabel);
  const [variablePopoverOpen, setVariablePopoverOpen] = useState(false);
  const [directivePopoverOpen, setDirectivePopoverOpen] = useState(false);
  const justInsertedDirectiveRef = useRef(false);

  const toolbarState = useEditorState({
    editor,
    selector: selectHeaderToolbarState,
  });

  const handleInsertVariable = useCallback(
    (variableName: string) => {
      if (editor == null) return;
      editor.chain().focus().run();
      editor
        .chain()
        .focus()
        .insertContent({
          type: "variableToken",
          attrs: { raw: variableName },
        })
        .run();
      onChange(htmlToMarkdown(editor.getHTML()));
      setVariablePopoverOpen(false);
    },
    [editor, onChange],
  );

  const handleInsertDirective = useCallback(
    (directiveKey: string) => {
      if (editor == null) return;
      justInsertedDirectiveRef.current = true;
      editor
        .chain()
        .insertContent({
          type: "variableToken",
          attrs: { raw: `@${directiveKey}/` },
        })
        .run();
      onChange(htmlToMarkdown(editor.getHTML()));
      setDirectivePopoverOpen(false);
    },
    [editor, onChange],
  );

  if (editor == null) return null;

  const stableVariables = variables ?? [];
  const availableDirectives = getAvailableDirectives(stableVariables);

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className="
          flex flex-nowrap items-center gap-1 border-b
          border-(--console-border-input) px-2 py-1
        "
      >
        {richTextEditorHeaderToolbarButtons({
          buttons,
          editor,
          isWysiwyg,
          modKeyLabel,
          shortcutKeys,
          toolbarState,
          variablePopoverOpen,
          setVariablePopoverOpen,
          directivePopoverOpen,
          setDirectivePopoverOpen,
          stableVariables,
          availableDirectives,
          onChange,
          handleInsertVariable,
          handleInsertDirective,
          justInsertedDirectiveRef,
        })}
        {showTrailingHelp ? (
          <>
            <span className="ml-auto" />
            {isWysiwyg ? (
              <KeyboardShortcutsModal>
                <ToolbarButton aria-label="Keyboard shortcuts">
                  <Keyboard className="size-4" />
                </ToolbarButton>
              </KeyboardShortcutsModal>
            ) : (
              <>
                <DirectiveReferenceModal>
                  <ToolbarButton aria-label="Directive variable reference">
                    <AtSign className="size-4" />
                  </ToolbarButton>
                </DirectiveReferenceModal>
                <MarkdownHelpModal>
                  <ToolbarButton aria-label="Markdown formatting help">
                    <Asterisk className="size-4" />
                  </ToolbarButton>
                </MarkdownHelpModal>
              </>
            )}
          </>
        ) : null}
      </div>
    </TooltipProvider>
  );
};
